/**
 * URL search-param serialization for 患者リスト(施術師毎).
 *
 * Dates are stored as ISO (YYYY-MM-DD) in the URL so a link can be pasted
 * without worrying about 和暦 encoding. The form still displays 和暦;
 * we convert on read/write.
 */

import { convGtJDate, dateValueToIso } from '@/utils/dateConverter';
import type { VisitPatientSearchFormValues } from './components/VisitPatientSearchForm';
import type { VisitPatientSearchParams } from './types';

/**
 * Build a URLSearchParams object from the current form state + payload.
 * Sets `run=1` so the page knows to auto-run the search on reload.
 */
export function formValuesToSearchParams(
  values: VisitPatientSearchFormValues,
  payload: VisitPatientSearchParams
): URLSearchParams {
  const qs = new URLSearchParams();
  qs.set('run', '1');

  // Dates are required — always written.
  qs.set('visit_date_from', payload.visit_date_from);
  qs.set('visit_date_to', payload.visit_date_to);

  if (payload.expiration_date_from) qs.set('expiration_date_from', payload.expiration_date_from);
  if (payload.expiration_date_to) qs.set('expiration_date_to', payload.expiration_date_to);
  if (payload.insurer_type_id != null) qs.set('insurer_type_id', String(payload.insurer_type_id));
  if (payload.age_from != null) qs.set('age_from', String(payload.age_from));
  if (payload.age_to != null) qs.set('age_to', String(payload.age_to));

  // Massager radio is '1' (none) or '2' (specified). Derive from the presence of massager_id.
  if (values.massagerRadio === '2' && payload.massager_id != null) {
    qs.set('massager_radio', '2');
    qs.set('massager_id', String(payload.massager_id));
  }

  return qs;
}

/**
 * Hydrate form values from URL. Returns `null` if the URL doesn't carry `run=1`,
 * signaling that the page should use the built-in defaults (first-of-month).
 */
export function searchParamsToFormValues(
  urlSearchParams: URLSearchParams
): VisitPatientSearchFormValues | null {
  if (urlSearchParams.get('run') !== '1') return null;

  const toWareki = (iso: string | null): string => (iso ? convGtJDate(iso) : '');

  const intOrNull = (raw: string | null): number | null => {
    if (raw == null || raw === '') return null;
    const n = Number(raw);
    return Number.isFinite(n) ? n : null;
  };

  const massagerRadio = urlSearchParams.get('massager_radio') === '2' ? '2' : '1';
  const massagerId = massagerRadio === '2' ? intOrNull(urlSearchParams.get('massager_id')) : null;

  return {
    visitDateFrom: toWareki(urlSearchParams.get('visit_date_from')),
    visitDateTo: toWareki(urlSearchParams.get('visit_date_to')),
    expirationDateFrom: toWareki(urlSearchParams.get('expiration_date_from')),
    expirationDateTo: toWareki(urlSearchParams.get('expiration_date_to')),
    insurerTypeId: intOrNull(urlSearchParams.get('insurer_type_id')),
    ageFrom: intOrNull(urlSearchParams.get('age_from')),
    ageTo: intOrNull(urlSearchParams.get('age_to')),
    massagerRadio,
    massagerId,
  };
}

/**
 * Convert hydrated form values back into the backend search payload.
 */
export function formValuesToSearchPayload(
  values: VisitPatientSearchFormValues
): VisitPatientSearchParams {
  const payload: VisitPatientSearchParams = {
    visit_date_from: dateValueToIso(values.visitDateFrom) ?? '',
    visit_date_to: dateValueToIso(values.visitDateTo) ?? '',
  };
  const expFrom = dateValueToIso(values.expirationDateFrom);
  const expTo = dateValueToIso(values.expirationDateTo);
  if (expFrom) payload.expiration_date_from = expFrom;
  if (expTo) payload.expiration_date_to = expTo;
  if (values.insurerTypeId != null) payload.insurer_type_id = values.insurerTypeId;
  if (values.ageFrom != null) payload.age_from = values.ageFrom;
  if (values.ageTo != null) payload.age_to = values.ageTo;
  if (values.massagerRadio === '2' && values.massagerId) {
    payload.massager_id = values.massagerId;
  }
  return payload;
}
